import { appKey } from '@app/shared/brand'
import { PRESENCE_FOLLOW_POLL_MS, PRESENCE_POLL_MS } from '@app/studio'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useTRPC } from '../../lib/trpc'

/**
 * Somebody else in the room, as the roster draws them.
 *
 * `at` is where they are IN THE BOOK — a block, never a scroll offset. Null
 * until they have said, which is not an error: a reader has a place in the room
 * before they have one in the text.
 */
export type LiveReader = {
  personId: string
  name: string
  at: string | null
}

/** Somebody leading the room, and how many came with them. */
export type LiveSpotlight = {
  /**
   * The spotlight itself, which a client remembers it BY.
   *
   * Distinct from whoever holds it, so a second spotlight from the same person
   * is a new offer rather than one this reader already declined.
   */
  id: string
  /** Null where this reader may not be told who is here — a public link. */
  leaderPersonId: string | null
  startedAt: string | Date
  /** How many readers said they came — counted, never inferred from position. */
  with: number
}

export type LiveBook = {
  /** Everybody else who is here and can be named. Never the reader themselves. */
  readers: LiveReader[]
  /** How many are here by public link, who are counted rather than named. */
  anonymous: number
  /** Whether that count has hit its ceiling and is a floor rather than a tally. */
  anonymousCapped: boolean
  /** Who is leading the book, if anybody is. */
  spotlight: LiveSpotlight | null
}

const EMPTY: LiveBook = {
  readers: [],
  anonymous: 0,
  anonymousCapped: false,
  spotlight: null,
}

/**
 * This tab, for as long as it is this tab.
 *
 * `sessionStorage` rather than `localStorage`, which two tabs share — that
 * would make two readers one row and each would keep overwriting the other. And
 * rather than component state, which a reload discards: refreshing a page is
 * not leaving the room, and a fresh id would leave the old row standing until
 * it went stale.
 */
export function presenceTabId(scope: 'signed-in' | 'by-link'): string {
  /*
   * Scoped by HOW the reader got here, because one tab can be both in turn: a
   * public link opened in the tab that was signed in, or the reverse. The row
   * is keyed on the tab alone, so one id across both would hand the same row to
   * two identities — leaving a reader counted as anonymous after they sign in,
   * or worse, a stranger wearing the name of whoever used the tab before.
   */
  const key = appKey('presence', 'tab', scope)
  const held = sessionStorage.getItem(key)
  if (held) return held
  const minted = crypto.randomUUID()
  sessionStorage.setItem(key, minted)
  return minted
}

/**
 * Who else has this book open.
 *
 * **The only shape a screen sees.** Nothing above this learns what carries it,
 * and the roster is read from the server's rows rather than assembled from what
 * arrived — so replacing the poll with a stream later is this file and no
 * screen.
 *
 * **Asked only while somebody is looking at the answer.** That is what
 * separates it from the timer `build.ts` refuses: that one would ask a question
 * nobody is waiting on, on a phone in shul. This runs on one page, while the
 * tab is visible, and stops when it is not — so a book left open in a
 * background tab costs nothing, and its reader drops out of everybody's roster
 * within the stale window, which is the truth about whether they are reading.
 *
 * A failure leaves the last answer standing rather than emptying the room: a
 * dropped request is not evidence that everybody left.
 */
export function useLiveBook(
  where: { bookId: string } | { token: string },
  /** Where this reader is, published on the same call. */
  at: string | null = null,
  /** Whether this reader is following somebody, which is worth asking faster for. */
  keen = false,
): LiveBook {
  const trpc = useTRPC()
  const [room, setRoom] = useState<LiveBook>(EMPTY)

  const byId = useMutation(trpc.share.presence.mutationOptions())
  const byToken = useMutation(trpc.share.presenceByToken.mutationOptions())

  const bookId = 'bookId' in where ? where.bookId : null
  const token = 'token' in where ? where.token : null

  const askById = byId.mutateAsync
  const askByToken = byToken.mutateAsync

  /*
   * Held rather than depended on. A position changes on every settle, and an
   * effect that restarted with it would tear down the interval and fire an
   * extra ask each time somebody stopped scrolling — turning a poll into a
   * request per movement, which is the thing publishing on a settle exists to
   * avoid. The next tick reads the current value.
   */
  const here = useRef(at)
  here.current = at

  useEffect(() => {
    let dropped = false
    let timer: ReturnType<typeof setInterval> | null = null

    const ask = async () => {
      if (document.visibilityState !== 'visible') return
      try {
        const next = await (bookId
          ? askById({
              bookId,
              tabId: presenceTabId('signed-in'),
              at: here.current,
            })
          : token
            ? askByToken({
                token,
                tabId: presenceTabId('by-link'),
                at: here.current,
              })
            : null)
        if (!dropped && next) setRoom(next)
      } catch {
        /*
         * A refusal is as likely to be a share just revoked as a network that
         * blinked, and neither is a reason to redraw the room. The reader's own
         * access is answered by the page around this, which asks separately.
         */
      }
    }

    const start = () => {
      if (timer !== null) return
      void ask()
      timer = setInterval(
        () => void ask(),
        keen ? PRESENCE_FOLLOW_POLL_MS : PRESENCE_POLL_MS,
      )
    }

    const stop = () => {
      if (timer === null) return
      clearInterval(timer)
      timer = null
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') start()
      else {
        stop()
        /* Hidden is not here. Emptying the roster now means a reader coming
           back does not read a room that was true a minute ago. */
        if (!dropped) setRoom(EMPTY)
      }
    }

    start()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      dropped = true
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [bookId, token, askById, askByToken, keen])

  return room
}
